"use client"

import { useCallback, useMemo } from "react"
import { useLocale, useMessages, useTranslations } from "next-intl"
import { SERVER_ERROR_KEYS } from "@/lib/i18n/server-error-keys"
import { AI_DISCLOSURE_VOICE_MESSAGE_ERROR_PREFIX } from "@/lib/ai-disclosure/errors"

const translatedErrorCache = new WeakMap<object, Map<string, Set<string>>>()
const emptyMessages = new Set<string>()

/** UI presentation only: callers retain the original error, status and validation paths. */
export function useTranslatedErrorMessage() {
  const locale = useLocale()
  const messages = useMessages()
  const t = useTranslations("ServerErrors")
  const common = useTranslations("Common")
  const unknownError = common("unknownError")
  const invalidValue = t("invalidValue")
  // Share the static catalog lookup across mutation hooks; no actor/request state enters this cache.
  const translatedMessages = useMemo(() => {
    if (locale === "en") return emptyMessages
    const catalog = messages
    let localeMessages = translatedErrorCache.get(catalog)
    if (!localeMessages) {
      localeMessages = new Map()
      translatedErrorCache.set(catalog, localeMessages)
    }
    const cached = localeMessages.get(locale)
    if (cached) return cached
    const translated = new Set<string>()
    collectCatalogStrings(catalog, translated)
    localeMessages.set(locale, translated)
    return translated
  }, [locale, messages])

  const message = useCallback((value: string) => {
    if (value.startsWith(AI_DISCLOSURE_VOICE_MESSAGE_ERROR_PREFIX)) {
      return t("aiDisclosureVoiceMessages", { agents: value.slice(AI_DISCLOSURE_VOICE_MESSAGE_ERROR_PREFIX.length) })
    }
    if (isKnownMessage(value)) return t(SERVER_ERROR_KEYS[value])
    if (locale === "en" || translatedMessages.has(value) || value === unknownError || value === invalidValue) return value
    return unknownError
  }, [invalidValue, locale, t, translatedMessages, unknownError])
  const field = useCallback((value: string) => {
    if (isKnownMessage(value)) return t(SERVER_ERROR_KEYS[value])
    if (locale === "en" || translatedMessages.has(value) || value === invalidValue) return value
    return invalidValue
  }, [invalidValue, locale, t, translatedMessages])
  return { message, field }
}

function isKnownMessage(message: string): message is keyof typeof SERVER_ERROR_KEYS {
  return Object.prototype.hasOwnProperty.call(SERVER_ERROR_KEYS, message)
}

function collectCatalogStrings(node: unknown, values: Set<string>): void {
  if (typeof node === "string") { values.add(node); return }
  if (node && typeof node === "object") {
    for (const value of Object.values(node)) collectCatalogStrings(value, values)
  }
}
